import React, { useState } from 'react';
import { File, Plus, Edit, Trash2 } from 'lucide-react';
import { emailTemplates } from '../../../data/emailTemplates';
import { EmailTemplate } from '../../../types/email';
import EmailTemplateEditor from './EmailTemplateEditor';

interface EmailTemplatesProps {
  onUseTemplate: (template: EmailTemplate) => void;
}

export default function EmailTemplates({ onUseTemplate }: EmailTemplatesProps) {
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [templates, setTemplates] = useState<EmailTemplate[]>(emailTemplates);

  const handleEditTemplate = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setShowEditor(true);
  };

  const handleDeleteTemplate = (templateId: string) => {
    if (confirm('Are you sure you want to delete this template?')) {
      setTemplates(templates.filter(t => t.id !== templateId));
    }
  };

  const handleSaveTemplate = (template: EmailTemplate) => {
    if (editingTemplate) {
      setTemplates(templates.map(t => t.id === template.id ? template : t));
    } else {
      setTemplates([...templates, { ...template, id: crypto.randomUUID() }]);
    }
    setShowEditor(false);
    setEditingTemplate(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={() => {
            setEditingTemplate(null);
            setShowEditor(true);
          }}
          className="flex items-center text-sm text-blue-600 hover:text-blue-700"
        >
          <Plus className="w-4 h-4 mr-1" />
          New Template
        </button>
      </div>

      <div className="space-y-2">
        {templates.map((template) => (
          <div
            key={template.id}
            className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg group"
          >
            <button
              onClick={() => onUseTemplate(template)}
              className="flex items-center flex-1 text-sm text-gray-600 hover:text-gray-900"
            >
              <File className="w-4 h-4 mr-2" />
              <span>{template.name}</span>
            </button>
            
            <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => handleEditTemplate(template)}
                className="p-1 text-gray-400 hover:text-blue-600"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDeleteTemplate(template.id)}
                className="p-1 text-gray-400 hover:text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showEditor && (
        <EmailTemplateEditor
          template={editingTemplate}
          onSave={handleSaveTemplate}
          onClose={() => {
            setShowEditor(false);
            setEditingTemplate(null);
          }}
        />
      )}
    </div>
  );
}